import React from 'react';
import Section from 'components/Section';
import SectionHeader from 'components/SectionHeader';
import Link from 'next/link';
import {
    HeaderH2,
    HeaderH1,
    Text,
    NextButton,
    ImageBlock
} from 'components/TextBlocks';
import NewsletterSection from './NewsletterSection';

const BlogCard = ({ title, date, type, ...props }) => {
    return (
        <div
            className="column is-4 blogCardStyle"
            style={{
                backgroundColor: '#FFF',
                marginBottom: '24px',
                boxShadow:
                    '0 2px 12px -1px rgba(219,221,223,0.60), 0 35px 54px -26px rgba(148,155,162,0.70)',
                borderRadius: '16px',
                width: '100%'
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '24px'
                }}
            >
                <div
                    style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        background: type === 'About' ? '#A0FFE9' : '#FDDD93',
                        color: type === 'About' ? '#034637' : '#583508',
                        borderRadius: '6px',
                        fontWeight: '600',
                        fontSize: '14px'
                    }}
                >
                    {type}
                </div>
                <div
                    style={{
                        fontWeight: '600',
                        fontSize: '14px',
                        marginLeft: 'auto',
                        color: '#646f7d'
                    }}
                >
                    {date}
                </div>
            </div>
            <h1
                className="title is-spaced"
                style={{
                    marginBottom: '1.25rem',
                    fontWeight: '700',
                    fontSize: '28px'
                }}
            >
                {title}
            </h1>
            {props.children}
        </div>
    );
};

const pStyle = {
    fontFamily: 'Rubik, sans-serif',
    color: '#646F7D',
    fontSize: '20px',
    fontWeight: '400',
    lineHeight: '1.925'
};

const keyCss = {
    fontSize: '1rem',
    fontFamily: 'menlo, consolas, monospace',
    border: '1px solid #DDDDDD',
    borderRadius: '5px',
    boxShadow: '0 4px 8px -1px rgba(201,204,208,0.90)',
    backgroundColor: '#FFF',
    paddingTop: '4px',
    paddingBottom: '4px',
    paddingLeft: '7px',
    paddingRight: '6px'
};

let FeatureRow = ({ title, desc, link, imgSrc, w, h }) => {
    return (
        <>
            <b>• {title} -</b> {desc}
            {link && (
                <Link href={link} style={{ color: '#0078ff' }}>
                    here
                </Link>
            )}
            {imgSrc && (
                <ImageBlock
                    src={imgSrc}
                    width={w}
                    height={h}
                    alt={title}
                    quality={100}
                />
            )}
            <br />
        </>
    );
};

const VerThreeThree = () => {
    return (
        <>
            <p className="subtitle" style={pStyle}>
                New version has arrived! Let's dive in:
            </p>
            <p className="subtitle" style={pStyle}>
                <FeatureRow
                    title={'Save images from the web'}
                    desc={'Our headliner for this update is the ability to save and display images from around the web. Read how to use this feature '}
                    imgSrc={'/guide/v33_images.png'}
                    link={'/guide/save-images'}
                    w={737}
                    h={691}
                />
                <FeatureRow
                    title={'Copy items or tabs with shift'}
                    desc={'You can now copy items by dragning while holding down shift, this will duplicate the item and keep the original in place. This also works with open tabs if you just want to save a tab but still keep it open.'}
                    imgSrc={'/guide/v33_copy.png'}
                    w={737}
                    h={491}
                />
                 <FeatureRow
                    title={'Hide stacks content'}
                    desc={'You can now hide the fav icons in a stack by clicking the hide icon in the top right corner of the stack. The open/close/hidden state now also persists across sessions.'}
                    imgSrc={'/guide/v33_hidestack.png'}
                    w={737}
                    h={491}
                />
                <FeatureRow
                    title={'Set custom fav icons for sites'}
                    desc={'Inside the tab modal you can now set a custom fav icon for the site, these can be uploaded via the settings panel(can also be used for groups and workspaces).'}
                    imgSrc={'/guide/v33_customfav.png'}
                    w={737}
                    h={491}
                />
                <FeatureRow
                    title={'Save and close option for all tabs'}
                    desc={'In the windows menu you can now find the save and close option for all tabs, this will save all tabs in the selected window and close them. To open the menu simply right-click the mini-window or click and hold for 0.4s.'}
                    imgSrc={'/guide/v33_saveclose.png'}
                    w={645}
                    h={405}
                />
                <FeatureRow
                    title={'Presist locked sidebar'}
                    desc={'Locking sidebar now persists across sessions, the change is accompanied by a new icon.'}
                />
                <FeatureRow
                    title={'New meta images fetcher'}
                    desc={'Improved meta image fetching to better handle sites with missing preview images.'}
                />
                <FeatureRow
                    title={'Translations'}
                    desc={'We have now translated the extension into 9 new languages, including Spanish 🇪🇸, French 🇫🇷, German 🇩🇪, Indonesian 🇮🇩, Japanese 🇯🇵, Polish 🇵🇱, Swedish 🇸🇪, Vietnamese 🇻🇳, and Brazilian Portuguese 🇧🇷. If you want your language to be added, please let us know.'}
                />
                <FeatureRow
                    title={'Bug fixes and improvements'}
                    desc={'Fixes includes a darkmode iconsistency, issue where you delete a attached note and whole item is deleted, and more.'}
                />
                {/* Add more FeatureRow components as needed */}
            </p>
            <p className="subtitle" style={pStyle}>
                Your extension should auto-update, but if it does not, you can
                go to chrome://extensions/ → Details → Update
            </p>
        </>
    );
};

const VerThreeTwo = () => {
    return (
        <>
            <p className="subtitle" style={pStyle}>
                Version 3.2 brings a new overview and a way to handle all your windows and their tabs, also better import support and bug fixes.
            </p>
            <p className="subtitle" style={pStyle}>
                <FeatureRow
                    title={'Window manager'}
                    desc={'In the left hand panel you can now see all your windows and their tabs. Windows can be created, closed and saved to your workspaces.'}
                    imgSrc={'/guide/tabwindows.png'}
                    w={842}
                    h={630}
                />
                <FeatureRow
                    title={'Import'}
                    desc={'You can now finally import exported workspaces yourself inside settings.'}
                    imgSrc={'/guide/export_import.png'}
                    w={706}
                    h={653}
                />
                <FeatureRow
                    title={'Bug fixes'}
                    desc={'A ton of bug fixes and improvements under the hood has been made to improve performance and stability.'}
                />
                {/* Add more FeatureRow components as needed */}
            </p>
            <p className="subtitle" style={pStyle}>
                Your extension should auto-update, but if it does not, you can
                go to chrome://extensions/ → Details → Update
            </p>
        </>
    );
};

const VerThree = () => {
    return (
        <>
            <p className="subtitle" style={pStyle}>
                Version 3 has arrived 🎉🎉🎉! The extension and its core
                architecture have been thoroughly revamped, delivering enhanced
                stability, improved performance, and a superior foundation for
                developing new features. The web app now incorporates many
                components of the extension, excluding the tab/tab groups and
                bookmark management features, but it enables reading, writing,
                sharing, setting and clearing reminders and more with your data.
            </p>
            <p className="subtitle" style={pStyle}>
                <FeatureRow
                    title={'Tabs list'}
                    desc={
                        'The whole interfaces has gotten more or less a facelift, the much imporant tabs list where you see your active tabs has been rewritten from scratch. Active tabs now show a popover where you can save and interact with your tabs.'
                    }
                    imgSrc={'/guide/tabslist.png'}
                    w={989}
                    h={655}
                />
                <FeatureRow
                    title={'Tabs groups'}
                    desc={
                        'The active tabs list now also shows your tab groups. You can now create tab groups from selected tabs, edit the name and set a symbol, save and manage them.'
                    }
                    imgSrc={'/guide/tabgroups.png'}
                    w={989}
                    h={655}
                />
                <FeatureRow
                    title={'Workspace grid'}
                    desc={
                        'The old workspace picker is now the workspace grid, its own view where you have a overview of all your workspaces and where you can quickly drag and drop categories to be moved around.'
                    }
                    imgSrc={'/guide/workspacegrid.png'}
                    w={1192}
                    h={740}
                />
                <FeatureRow
                    title={'Search'}
                    desc={
                        'Search is now its own separate thing and can be found via the search icon in the right hand corner or using the shortcut keys ⌘/Ctrl + K'
                    }
                    imgSrc={'/guide/search.png'}
                    w={1192}
                    h={740}
                />
                {/* <FeatureRow
                    title={'Menus'}
                    desc={'The menus have been updated and are now more powerful'}
                    imgSrc={'/guide/settingsmenu.png'}
                    w={742}
                    h={642}
                /> */}
                <FeatureRow
                    title={'Global size settings'}
                    desc={
                        'Settings will be more powerful than ever so you can modify more of tabExtend to your own liking. To start out we have removed the old compact mode and replaced it with a new global size setting for items.'
                    }
                    imgSrc={'/guide/sizesettings.png'}
                    w={488}
                    h={740}
                />
            </p>
            <p className="subtitle" style={pStyle}></p>
            <p className="subtitle" style={pStyle}>
                Your extension should auto-update, but if it does not, you can
                go to chrome://extensions/ → Details → Update
            </p>
        </>
    );
};

const VerTwoFour = () => {
    return (
        <>
            <p className="subtitle" style={pStyle}>
                Wrapping up 2023 with a last update, v.2.4. Here are the
                highlights:
            </p>
            <p className="subtitle" style={pStyle}>
                <FeatureRow
                    title={'Pin active tabs sidebar'}
                    desc={
                        'Pin the active tabs sidebar and disable closing of it when hovering out, find the pin icon in the top right corner of the sidebar.'
                    }
                    imgSrc={'/guide/pin_sidebar.png'}
                    w={686}
                    h={481}
                />
                <FeatureRow
                    title={
                        'Resize groups indicator + Equalize widths by double clicking'
                    }
                    desc={
                        'The group resize-indicator is now easier to spot and by double clicking the indicator you can equalize the widths of all groups in the category to the same width as the clicked group.'
                    }
                    imgSrc={'/guide/resize_group.png'}
                    w={686}
                    h={481}
                />
                <FeatureRow
                    title={'Unstack all items in a combined row'}
                    desc={
                        'Select one or more combined rows and click the unstack icon to unstack all items.'
                    }
                    imgSrc={'/guide/unstack.png'}
                    w={686}
                    h={481}
                />
                <FeatureRow
                    title={'Right-click urls on sites and save as'}
                    desc={
                        'You can now right-click urls on sites and save them as sites into existing groups or new groups.'
                    }
                />
            </p>
            <p className="subtitle" style={pStyle}>
                Your extension should auto-update, but if it does not, you can
                go to chrome://extensions/ → Details → Update
            </p>
        </>
    );
};

const VerTwoThree = () => {
    return (
        <>
            <p className="subtitle" style={pStyle}>
                Version 2.3 is here! bringing a few new user requested features,
                like collapsing groups and more export options, also some
                imporant bug fixes and touch ups. Here are the highlights:
            </p>
            <p className="subtitle" style={pStyle}>
                <FeatureRow
                    title={'Collapse groups'}
                    desc={
                        'Collapse groups to save space and make it easier to navigate your groups. You can collapse a group by clicking the collapse icon in the top right corner of the group.'
                    }
                    imgSrc={'/guide/collapse_groups.png'}
                    w={1236}
                    h={865}
                />
                <FeatureRow
                    title={'See meta image of active tab'}
                    desc={
                        'The hover tooltip for active tabs have been updated to show the meta image of the site instead of the screenshot API we used before.'
                    }
                    imgSrc={'/guide/active_hover_meta.png'}
                    w={838}
                    h={632}
                />
                <FeatureRow
                    title={'Refetch favicon'}
                    desc={
                        'If you have a site that has a broken favicon, you can now refetch it by clicking the refresh icon in the top right corner of the site.'
                    }
                    imgSrc={'/guide/refresh_favicon.png'}
                    w={446}
                    h={376}
                />
                <FeatureRow
                    title={'More export formats'}
                    desc={
                        'You can now export your workspaces in HTML or CSV format, found in options → Export current workspace.'
                    }
                    imgSrc={'/guide/export_options.png'}
                    w={610}
                    h={474}
                />
            </p>
        </>
    );
};
const VerTwoOne = () => {
    return (
        <>
            <p className="subtitle" style={pStyle}>
                Version 2.1 introduces reminders, a new way to keep track of
                your sites, notes and to-dos. Also, the mobile apps get a huge
                upgrade making it possible to create groups and add sites on the
                go.
            </p>
            <p className="subtitle" style={pStyle}>
                <FeatureRow
                    title={'Reminders for sites and items'}
                    desc={
                        'Setup one-time reminders or repeating reminders for sites, notes and to-dos. Learn the details on how-to here in the guide section '
                    }
                    link={'/guide/reminders'}
                    imgSrc={'/guide/reminders_overview.png'}
                    w={1372}
                    h={884}
                />
                {/* <ImageBlock
          src={"/guide/set_reminder.png"}
          width={690}
          height={660}
          alt={"Set reminder"}
          quality={100}
          mtLessExtra
        /> */}
                <FeatureRow
                    title={'Create groups in mobile apps'}
                    desc={'Create groups in the iOS and Andriod apps'}
                    imgSrc={'/guide/create_group_mobile.png'}
                    w={897}
                    h={857}
                />
                <FeatureRow
                    title={'Add notes'}
                    desc={
                        'Also new in the mobile apps, add notes and set a color for them'
                    }
                    /* imgSrc={""}
          w={614}
          h={532} */
                />
                <FeatureRow
                    title={'Share to'}
                    desc={
                        'Last but not least you can now use the "share to" feature in your mobile browser to quickly add sites to tabExtend(Please note that this feature is currently only available in the iOS app)'
                    }
                    imgSrc={'/guide/share_to_view.png'}
                    w={428}
                    h={436}
                />
            </p>
            <p className="subtitle" style={pStyle}>
                Your extension should auto-update, but if it does not, you can
                go to chrome://extensions/ → Details → Update
            </p>
        </>
    );
};
const VerTwo = () => {
    return (
        <>
            <p className="subtitle" style={pStyle}>
                Version 2.0 is here, bringing a few sought-after features and a
                rewrite in the background to enable better performance and
                real-time syncing with the upcoming mobile apps.
            </p>
            <p className="subtitle" style={pStyle}>
                <FeatureRow
                    title={'Open all links in a stack'}
                    desc={
                        'Use the open iconed button that appears on hover to open all tabs in a stack'
                    }
                    imgSrc={'/guide/open_all_stacked.png'}
                    w={614}
                    h={532}
                />
                <FeatureRow
                    title={'Meta images and description'}
                    desc={
                        'The tab modal has gotten a bit of a facelift, most notably you can now see the meta image + description of the site. Furthermore, the meta image can be pinned and always shown in your group, giving way for an easier way to quickly scan your sites in your overview.'
                    }
                    imgSrc={'/guide/meta_image.png'}
                    w={1022}
                    h={769}
                />
                <FeatureRow
                    title={'Merge groups'}
                    desc={
                        'You can now merge two groups by dragging and dropping one group on top of another, the first will merge into the second group. A quick way to clean up your categories.'
                    }
                    imgSrc={'/guide/merge_groups.png'}
                    w={747}
                    h={769}
                />
                <FeatureRow
                    title={'Design refresh'}
                    desc={
                        "It's all in the details. The user interface has received a minor but necessary design touchup."
                    }
                    imgSrc={'/guide/design_refresh.png'}
                    w={581}
                    h={581}
                />
            </p>
            <p className="subtitle" style={pStyle}>
                Your extension should auto-update, but if it does not, you can
                go to chrome://extensions/ → Details → Update
            </p>
        </>
    );
};

const VerOneNine = () => {
    return (
        <>
            <p className="subtitle" style={pStyle}>
                The last major version of v1.x is here, and it features a new
                way of interacting with tabExtend. You can now save and add
                notes from the tabExtend popover. The popover can be opened on
                top of any website(excluding the new tab, Chrome store, and
                other browser settings pages). Here are a few pointers on how to
                use it:
            </p>
            <p className="subtitle" style={pStyle}>
                <FeatureRow
                    title={'Open tabExtend popover'}
                    desc={
                        'To open the popover you can either click the tabExtend icon in the extensions panel or use the shortcut: Cmd + U (Mac)/Ctrl + U(PC)'
                    }
                    imgSrc={'/guide/toggle_popover.png'}
                    w={231}
                    h={145}
                />
                <FeatureRow
                    title={'Overview'}
                    desc={
                        'Once the popover is open, you can drag the popover, add notes, toggle dark mode, add to-do and colors.'
                    }
                    imgSrc={'/guide/popover_buttons.png'}
                    w={620}
                    h={463}
                />
                <FeatureRow
                    title={'Save site and note'}
                    desc={
                        'When you click save, you will see your library of workspaces and categories. You can then choose to save to an existing group or create a new group.'
                    }
                    imgSrc={'/guide/save_to_group.png'}
                    w={884}
                    h={615}
                />
                <FeatureRow
                    title={'See saved sites and notes'}
                    desc={
                        "If you open the popover on an already saved website, you will see where it's saved and if there are any notes. This also works for items that are saved in a shared workspace."
                    }
                    imgSrc={'/guide/saved_item.png'}
                    w={620}
                    h={493}
                />
                <FeatureRow
                    title={'Open tabExtend dashboard'}
                    desc={
                        'In the bottom-right corner, you can find a button that will take you to the tabExtend dashboard.'
                    }
                    imgSrc={'/guide/go_to_dashboard.png'}
                    w={151}
                    h={156}
                />
            </p>
            <p className="subtitle" style={pStyle}>
                Your extension should auto-update, but if it does not, you can
                go to chrome://extensions/ → Details → Update
            </p>
        </>
    );
};

const VerOneEight = () => {
    return (
        <>
            <p className="subtitle" style={pStyle}>
                New version is out today with a mix of useful and fun stuff.
                Here are the highlights:
            </p>
            <p className="subtitle" style={pStyle}>
                <FeatureRow
                    title={'Bin'}
                    desc={
                        'Contains your latest deleted items(up to 50). Select items to be restored to the currently selected category. Open via the actions menu in the top right corner, or the shortcut Ctrl + Z. Read more '
                    }
                    link={'/guide/bin'}
                    imgSrc={'/guide/bin.png'}
                    w={662}
                    h={446}
                />
                <FeatureRow
                    title={'Custom shortcuts'}
                    desc={
                        'Want to change that CTRL+Z shortcut? Change the shortcuts after your likings in the keybindings modal found in settings. Read about all shortcuts '
                    }
                    link={'/guide/shortcuts'}
                    imgSrc={'/guide/custom_shortcuts.png'}
                    w={577}
                    h={446}
                />
                <FeatureRow
                    title={'Custom emojis'}
                    desc={
                        'Upload your own images to be used as symbols for groups or workspaces. Read the how-to guide '
                    }
                    link={'/guide/custom-emojis'}
                    imgSrc={'/guide/custom_emojis.png'}
                    w={456}
                    h={190}
                />
                <FeatureRow
                    title={'Duplicate group'}
                    desc={
                        'Duplicate a group and use as a template or for moving to another workspace. Found inside the group menu:'
                    }
                    imgSrc={'/guide/duplicate_group.png'}
                    w={308}
                    h={342}
                />
                <FeatureRow
                    title={'Lock group'}
                    desc={
                        'Another group option: lock down your group to disable new items from being added or existing ones being moved.'
                    }
                    imgSrc={'/guide/lock_group.png'}
                    w={456}
                    h={190}
                />
                <FeatureRow
                    title={'Stack title'}
                    desc={
                        'Add titles to your stacked sites. Expand the row and click the title current title to do this.'
                    }
                    imgSrc={'/guide/stacked_title.png'}
                    w={456}
                    h={119}
                />
                <FeatureRow
                    title={'Workspace in site title'}
                    desc={
                        'See what workspace you are currently in at a glance in tabExtend title'
                    }
                    imgSrc={'/guide/title_workspace.png'}
                    w={416}
                    h={79}
                />
                <FeatureRow
                    title={'Longer notes and bugs'}
                    desc={
                        'Up to 5000 character notes and a bunch of bugs fixed, including categories not being scrollable on some smaller screens.'
                    }
                />
            </p>
            <p className="subtitle" style={pStyle}>
                As always, your extension should auto-update, but if it does
                not, you can go to chrome://extensions/ → Details → Update
            </p>
        </>
    );
};

const VerOneSevenFive = () => {
    return (
        <>
            <p className="subtitle" style={pStyle}>
                Side-by-side sites, trashbin drop-zone, and local file links.
                It's that time again, new version, new stuff:
            </p>
            <p className="subtitle" style={pStyle}>
                <b>• Side-by-side sites -</b> Drop a site on top of another site
                to combine them into a stacked item. You can also achieve this
                by selecting multiple sites and clicking the combine icon. Read
                more in the guide{' '}
                <Link href="/guide/combine" style={{ color: '#0078ff' }}>
                    here
                </Link>
                <ImageBlock
                    src={'/guide/stacked.png'}
                    width={421}
                    height={294}
                    alt={'Side by side sites'}
                    quality={100}
                />
                <br />
                <b>• Turn note with URL into site item -</b> When you paste a
                URL into a note, you can now choose to turn that note into a
                site. If there are any other notes inside they will be attached
                as a note to the site.
                <ImageBlock
                    src={'/guide/turn_into_link.png'}
                    width={327}
                    height={157}
                    alt={'Turn into link'}
                    quality={100}
                />
                <br />
                <b>• Trashbin drop-zone -</b> A new setting that, when
                activated, will give you a drop-zone to delete selected
                item/items that you drop inside.
                <ImageBlock
                    src={'/guide/trashbin_dropzone.png'}
                    width={426}
                    height={160}
                    alt={'Trashbin dropzone'}
                    quality={100}
                />
                <br />
                <b>• Show workspace symbol -</b> Another new option is to always
                show the current workspace symbol inside the workspace picker
                button next to the categories.
                <ImageBlock
                    src={'/guide/workspace_icon.png'}
                    width={329}
                    height={178}
                    alt={'Workspace icon'}
                    quality={100}
                />
                <br />
                <b>• Add file:/// links -</b> You can now add links to your
                local files by clicking "Add site" in the group menu.
                <br />
                <b>• Predefined .new links -</b> Inside Add site you can also
                choose from a list of predefined .new links to some popular apps
                that you might be handy.
                <ImageBlock
                    src={'/guide/predefined_links.png'}
                    width={662}
                    height={582}
                    alt={'Predefined new links'}
                    quality={100}
                />
                <br />
                <b>• Custom scroll bars -</b> Custom scrollbars added to more
                places to better align with the overall design.
                <br />
                <b>• Webapp link previews on hover - </b> Link previews is now
                available in your web dashboard(previously only on shared
                categories)
                <br />
                As always, your extension should auto-update, but if it does
                not, you can go to chrome://extensions/ → Details → Update
            </p>
        </>
    );
};

const VerOneSeven = () => {
    return (
        <>
            <p className="subtitle" style={pStyle}>
                New year, new version, almost exactly a year ago since the
                release of v1.0. We have a couple of new things:
            </p>
            <p className="subtitle" style={pStyle}>
                <b>• Share categories via link -</b> Choose to share publicly:
                anyone with the link can access the site. Alternatively,
                invite-only: invite anyone with an account to access the
                category website. The category site also has a preview link
                popover.
                <ImageBlock
                    src={'/guide/share_category.png'}
                    width={693}
                    height={618}
                    alt={'Share category update'}
                    quality={100}
                />
                <br />
                <b>• New workspace-picker and overview -</b> To open either
                click the workspace icon next to the categories or use the
                shortcut: On Mac: <span style={keyCss}>⌘</span> +{' '}
                <span style={keyCss}>K</span> On PC:{' '}
                <span style={keyCss}>Ctrl</span> + <span style={keyCss}>⇧</span>{' '}
                +<span style={keyCss}>K</span>. Inside you can use your arrow
                keys(
                <span style={keyCss}>←</span>
                <span style={keyCss}>↑</span>
                <span style={keyCss}>→</span>
                <span style={keyCss}>↓</span> ) to move arround +{' '}
                <span style={keyCss}>Enter</span> to switch category and
                workspace
                <ImageBlock
                    src={'/guide/workspace_picker.png'}
                    width={1119}
                    height={938}
                    alt={'New colorpicker'}
                    quality={100}
                />
                <br />
                <b>• Global search -</b> Inside the workspace-picker you also
                find the improved search that now searches all your workspaces.
                <br />
                <b>• Sync tabgroups links -</b> If you open up a tabgroup from
                tabExtend and add or remove links from it you now have the
                option to save and sync the content of the group.
                <ImageBlock
                    src={'/guide/syncbackgroup.png'}
                    width={400}
                    height={248}
                    alt={'Sync-back group'}
                    quality={100}
                />
                <br />
                <b>• Option to open links in new tab and jump to opened tab</b>
                <br />
                <b>• Bookmarks logic improved and other bugs -</b> A mixed bag
                of all-around improvments and bugs solved.
                <br />
            </p>
        </>
    );
};

const VerOneSixFive = () => {
    return (
        <>
            <p className="subtitle" style={pStyle}>
                A new version is out with these updates:
            </p>
            <p className="subtitle" style={pStyle}>
                • Multi select for multi drag and mass action
                <ImageBlock
                    src={'/guide/multidrag_short.gif'}
                    width={602}
                    height={740}
                    alt={'Multidrag support'}
                    quality={100}
                />
                <br />• Hold <span style={keyCss}>Cntrl</span> or{' '}
                <span style={keyCss}>Shift</span> and click items you want to
                select.
                <ImageBlock
                    src={'/guide/mass_actions.png'}
                    width={732}
                    height={482}
                    alt={'Do mass actions'}
                    quality={100}
                />
                <br />
                • New color-picker with more colors for your notes.
                <ImageBlock
                    src={'/guide/colorpicker.png'}
                    width={381}
                    height={436}
                    alt={'New colorpicker'}
                    quality={100}
                />
                • Added support for unlimited workspaces
                <br />
                • Saved sites now have a timestamp for when it was saved, just
                like notes.
                <br />
            </p>
        </>
    );
};

const VerOneSix = () => {
    return (
        <>
            <p className="subtitle" style={pStyle}>
                After a lot of work we are excited to present a new major
                version of tabExtend. Here's what's new:
            </p>
            <p className="subtitle" style={pStyle}>
                • Shared workspaces with realtime collaboration. Invite
                colleagues and friends to work on shared projects. Read how it
                works <Link href="/guide/share-workspaces">here</Link>
                <ImageBlock
                    src={'/guide/shared_workspace_modal.png'}
                    width={721}
                    height={457}
                    alt={'Workspace modal'}
                    quality={100}
                />
                • Sort items in group alphabetically or by date created, find
                the sort function in the group menu
                <br />
                <ImageBlock
                    src={'/guide/sort_items.png'}
                    width={432}
                    height={463}
                    alt={'Sort items in group'}
                    quality={100}
                />
                • Increased backup frequency. All data is now backed up daily
                <br />
                • Major performance improvment for quicker start and loading of
                workspaces
                <br />
                • Add slack:// and other custom hosts as links
                <br />
                • Increase the limit of workspaces(max 24)
                <br />• Hold <span style={keyCss}>Shift</span> +{' '}
                <span style={keyCss}>delete/backspace</span> to delete note
                <br />• <span style={keyCss}>Cntrl</span> +{' '}
                <span style={keyCss}>G</span> to create a new group
                <br />• Use <span style={keyCss}>1</span>
                <span style={keyCss}>2</span>
                <span style={keyCss}>3</span> etc to change selected category
                <br />
                • Saving tabs to a new group will put the group in the first
                slot(previously last)
                <br />
                • UI changes, including a share button and better contrast when
                using dark theme
                <br />
                <ImageBlock
                    src={'/guide/share_button.png'}
                    width={504}
                    height={292}
                    alt={'Share button'}
                />
            </p>
            <p className="subtitle" style={pStyle}>
                As always, your extension should auto-update, but if it does
                not, you can go to
                <br />{' '}
                <span style={{ fontWeight: '500' }}>
                    chrome://extensions/ → Details → Update
                </span>
            </p>
            <p className="subtitle" style={pStyle}></p>
        </>
    );
};

const VerOneFive = () => {
    return (
        <>
            <p className="subtitle" style={pStyle}>
                It's time for a new version! We are very excited to present
                v.1.5. These are all of the new features.
            </p>
            <p className="subtitle" style={pStyle}>
                • Workspaces(Only available for user on the Pro plan), create
                different sets of categories for different use cases. Learn more
                about workspaces <Link href="/guide/workspaces">here</Link>
                <br />
                <ImageBlock
                    src={'/guide/workspaces.png'}
                    width={534}
                    height={441}
                    quality={100}
                    alt={'workspaces'}
                />
                • Move groups to other workspaces
                <br />
                <ImageBlock
                    src={'/guide/movetocat.png'}
                    width={399}
                    height={641}
                    quality={100}
                    alt={'Move to workspace'}
                />
                • Import bookmarks, create groups out of your existing bookmarks
                folders.
                <br />
                <ImageBlock
                    src={'/guide/bookmarksGroup.png'}
                    width={909}
                    height={833}
                    quality={100}
                    alt={'Import bookmarks'}
                />
                • Notes persists the previous type(note/to-do) when pressing
                enter and creating a new one
                <br />
                • Scrollable categories when screen or window is too small
                <br />
                • Some UI touch-ups, including new design of the category modal
                <br />
                • Bugs fixes, including cold startup not loading user data.
                <br />
            </p>
            <p className="subtitle" style={pStyle}></p>
        </>
    );
};

const VerOneFour = () => {
    return (
        <>
            <p className="subtitle" style={pStyle}>
                New version is here with some highly requested features 🧞‍♂️.
            </p>
            <p className="subtitle" style={pStyle}>
                • Edit site modal that lets you edit Title and URL of a saved
                site.
                <br />
                • Move site to another group in another category.
                <br />
                <ImageBlock
                    src={'/guide/Editsite_modal.png'}
                    width={634}
                    height={363}
                    quality={100}
                    alt={'Edit site modal'}
                />
                • Compact view, hide URL, and make a group items take less
                space.
                <br />
                <ImageBlock
                    src={'/guide/Compact_view_option.png'}
                    width={399}
                    height={429}
                    quality={100}
                    alt={'Compact view for group'}
                />
                • Option to always open sites in a new tab.
                <br />
                <ImageBlock
                    src={'/guide/open_new_tab_option.png'}
                    width={444}
                    height={363}
                    quality={100}
                    alt={'Options'}
                />
                • When writing a note, pressing the Enter key now creates a new
                note(+ double tap puts the new note at the top of the group). To
                create a new row press Shift+Enter. Pressing Enter twice puts
                the note at the top of the group.
                <br />
                • Small UI changes and bug fixes including custom scroll inside
                notes and formatting bugs for arrows and bullet-point lists
                <br />
            </p>
            <p className="subtitle" style={pStyle}></p>
        </>
    );
};

const VerOneThree = () => {
    return (
        <>
            <p className="subtitle" style={pStyle}>
                New update with a focus on making you even quicker working in
                the browser.
            </p>
            <p className="subtitle" style={pStyle}>
                • Groups are now sorted under categories in the right-click
                close and save menu. You can also create a new group from the
                same menu.
                <br />
                <ImageBlock
                    src={'/guide/close_n_save_categories.png'}
                    width={587}
                    height={404}
                    quality={100}
                    alt={'Create new group by right clicking'}
                />
                • Open the edit category modal by long pressing on a category.
                <br />
                <ImageBlock
                    src={'/guide/open_categories_modal.png'}
                    width={655}
                    height={98}
                    quality={100}
                    alt={'Long press to open categories modal'}
                />
                • Multiple word shortcuts added. To quickly transform your notes
                write: /todo /done /red /orange /green
                <br />
                <ImageBlock
                    src={'/guide/task_done.png'}
                    width={458}
                    height={98}
                    quality={100}
                    alt={'Notes shortcuts'}
                />
                • Added auto as an alternative to the theme preferences
                <br />
                <ImageBlock
                    src={'/guide/theme_auto.png'}
                    width={458}
                    height={98}
                    quality={100}
                    alt={'Notes shortcuts'}
                />
                • Various bugs squashed, including right-click closing the last
                tab in the window will now be saved.
                <br />
            </p>
            <p className="subtitle" style={pStyle}>
                If the extension does not auto-update you can go to
                <br />{' '}
                <span style={{ fontWeight: '500' }}>
                    chrome://extensions/ → Details → Update
                </span>
            </p>
        </>
    );
};

const VerOneTwo = () => {
    return (
        <>
            <p className="subtitle" style={pStyle}>
                It's about that time again, a new version is out! Here is the
                list of all improvements:
            </p>
            <p className="subtitle" style={pStyle}>
                • Open all sites from a group in a new window(with the preserved
                position from when the group was saved)
                <br />
                • Significantly faster loading time
                <br />• Cmd + K now opens search, Cntrl clicking a link opens
                the selected site in a new tab.
                <br />• Custom styled scrollbars and other small improvements to
                groups
                <br />• Fixed emojipicker bug when the category was scrolled.
            </p>
            <p className="subtitle" style={pStyle}>
                If the extension does not auto-update you can go to
                <br />{' '}
                <span style={{ fontWeight: '500' }}>
                    chrome://extensions/ → Details → Update
                </span>
            </p>
        </>
    );
};

const VerOneOneFive = () => {
    return (
        <>
            <p className="subtitle" style={pStyle}>
                The new version includes:
            </p>
            <p className="subtitle" style={pStyle}>
                • Opening all sites now open as a group(Chrome and Edge version
                88+).
                <br />
                • Right-click on any site to close and save site to a chosen
                group.
                <ImageBlock
                    src={'/guide/close_and_save.png'}
                    width={504}
                    height={372}
                    quality={100}
                    alt={'Move tabs into group'}
                />
            </p>
        </>
    );
};

const VerOneOne = () => {
    return (
        <>
            <p className="subtitle" style={pStyle}>
                The new version includes:
            </p>
            <p className="subtitle" style={pStyle}>
                • A new easier way of opening and closing sites
                <br />
                • You can now add custom sites with your own title and URL to a
                group
                <br />• Minor bug fixes and improvements
            </p>
            <p className="subtitle" style={pStyle}>
                If the extension does not auto-update you can go to
                <br />{' '}
                <span style={{ fontWeight: '500' }}>
                    chrome://extensions/ → Details → Update
                </span>
            </p>
        </>
    );
};

const VerOne = () => {
    return (
        <>
            <p className="subtitle" style={pStyle}>
                We are excited to announce version 1.0 of tabExtend 🎉.
            </p>
            <p className="subtitle" style={pStyle}>
                The first version includes features like:
                <br />
                • Organize, save and open tabs
                <br />
                • Create notes/to-dos
                <br />
                • Mark text on any webpage, right-click and save it to a chosen
                group
                <br />
                • Create/delete/move groups and categories
                <br />
                • Light/Dark mode
                <br />
                • Fx sounds
                <br />
                • Thumbnails for active sites
                <br />
                • Combine items
                <br />
                • Colored notes/to-dos
                <br />
                • Auto-sync to cloud
                <br />
                • Resizable groups
                <br />
                • Offline mode
                <br />
                • Export data as JSON
                <br />
                • Webapp optimized for mobile
                <br />
                • Copy all content in a group to clipboard
                <br />
                • Add group with your top sites
                <br />
            </p>
            <p className="subtitle" style={pStyle}>
                See <Link href="/guide/getting-started">Getting started</Link>{' '}
                for instructions on how to add the extension to your browser and
                other features.
            </p>
            <p className="subtitle" style={pStyle}>
                All feedback and questions are more than welcome.
            </p>
        </>
    );
};

const IntroText = () => {
    return (
        <>
            <p className="subtitle" style={pStyle}>
                Focus is key for achieving your goals. Have you ever browsed the
                internet and been caught in the
                "open-in-a-new-tab-and-never-get-around-to-read-it-until-the-computer-crashes"
                rabbit hole? We certainly been there. With the world at your
                fingertips nothing is holding you back more than losing your
                focus. You can open up new amazing tools, communities, videos,
                courses, articles, etc, each in their own tab.
            </p>
            <p className="subtitle" style={pStyle}>
                Is having all these tabs good for productivity though? Certainly
                not. Do you often lose time by having to go back and search for
                information that you forgot where it was found? Do you often
                lose focus when jumping between tabs, or when opening another
                app to write notes or save something important?{' '}
            </p>
            <p className="subtitle" style={pStyle}>
                tabExtend lives inside your browser and the idea is that it will
                act as a drop-zone for your tabs, thoughts, and goals. Use it as
                your own kanban board, quickly launch your favorite sites,
                categorize and save new ones. Start each day with a clean slate,
                no leftover tabs letting you focus on your goals for the day.
            </p>
            {/*            <p className="subtitle" style={{fontFamily: "Rubik", color: "#646F7D", fontSize: "1.275rem", fontWeight: "400", lineHeight: "1.65"}}>
           Read more about all the features and how you use them
           <Link href="/">
           <a> here</a>
           </Link>
           </p> */}
            <p
                className="subtitle"
                style={{
                    fontFamily: 'Rubik, sans-serif',
                    color: '#646F7D',
                    fontSize: '20px',
                    fontWeight: '400',
                    lineHeight: '1.65',
                    fontStyle: 'italic'
                }}
            >
                Read more about all the features and how to use them in the{' '}
                <Link href="/guide/">guide section</Link>.
            </p>
            <h1
                className="title is-spaced"
                style={{
                    marginTop: '4rem',
                    marginBottom: '1.25rem',
                    fontWeight: '700',
                    fontSize: '28px'
                }}
            >
                Built with security and privacy in mind
            </h1>
            <p className="subtitle" style={pStyle}>
                Our vision is to bring you an outstanding app, with support,
                great security, continuously new helpful features, and
                integrations. To fulfill this vision we need to spend time on
                development, servers, and other tools. By charging a fee from
                pro users, we will finance the project, not by selling any data,
                tracking users, or showing you advertisements.
            </p>
            <p className="subtitle" style={pStyle}>
                Our business is independent and self-funded. We aim to earn your
                trust by always thinking and serving you, the user, first. The
                app have a generous free tier that has all features available
                for you to try out with no time limit.
            </p>
            <p className="subtitle" style={{ marginBottom: '3rem', ...pStyle }}>
                Please feel free to
                <a href="mailto:info@tabextend.com"> contact us</a> if you have
                any questions, suggestions, or just want to say hello. Cheers!
            </p>
        </>
    );
};

function UpdatesSection(props) {
    return (
        <>
            {/* <NewsletterSection /> */}
            <section
                className="SectionComponent hero section is-block is-relative py-8" /* style={{background: "#0078FF"}} */
            >
                <div className="container has-text-left">
                    <div
                        className="is-vcentered is-centered is-variable is-8 is-multiline py-3"
                        style={{
                            maxWidth: '940px',
                            marginLeft: 'auto',
                            marginRight: 'auto'
                        }}
                    >
                        <BlogCard
                         title={'Version 3.3.0'}
                         type={'Release note'}
                         date={'2024-11-20'}
                        >
                            <VerThreeThree />
                        </BlogCard>
                        <BlogCard
                            title={'Version 3.2.0'}
                            type={'Release note'}
                            date={'2024-08-06'}
                        >
                            <VerThreeTwo />
                        </BlogCard>
                        <BlogCard
                            title={'Version 3.0.0'}
                            type={'Release note'}
                            date={'2024-04-26'}
                        >
                            <VerThree />
                        </BlogCard>
                        <BlogCard
                            title={'Version 2.4.0'}
                            type={'Release note'}
                            date={'2023-12-21'}
                        >
                            <VerTwoFour />
                        </BlogCard>
                        <BlogCard
                            title={'Version 2.3.0'}
                            type={'Release note'}
                            date={'2023-12-08'}
                        >
                            <VerTwoThree />
                        </BlogCard>
                        <BlogCard
                            title={'Version 2.1.0'}
                            type={'Release note'}
                            date={'2023-08-30'}
                        >
                            <VerTwoOne />
                        </BlogCard>
                        <BlogCard
                            title={'Version 2.0.0'}
                            type={'Release note'}
                            date={'2023-05-12'}
                        >
                            <VerTwo />
                        </BlogCard>
                        <BlogCard
                            title={'Version 1.9.0'}
                            type={'Release note'}
                            date={'2022-05-25'}
                        >
                            <VerOneNine />
                        </BlogCard>
                        <BlogCard
                            title={'Version 1.8.0'}
                            type={'Release note'}
                            date={'2022-04-25'}
                        >
                            <VerOneEight />
                        </BlogCard>
                        <BlogCard
                            title={'Version 1.7.5'}
                            type={'Release note'}
                            date={'2022-03-10'}
                        >
                            <VerOneSevenFive />
                        </BlogCard>
                        <BlogCard
                            title={'Version 1.7.0'}
                            type={'Release note'}
                            date={'2022-01-21'}
                        >
                            <VerOneSeven />
                        </BlogCard>
                        <BlogCard
                            title={'Version 1.6.5'}
                            type={'Release note'}
                            date={'2021-10-01'}
                        >
                            <VerOneSixFive />
                        </BlogCard>
                        <BlogCard
                            title={'Version 1.6.0'}
                            type={'Release note'}
                            date={'2021-09-07'}
                        >
                            <VerOneSix />
                        </BlogCard>
                        <BlogCard
                            title={'Version 1.5.0'}
                            type={'Release note'}
                            date={'2021-05-21'}
                        >
                            <VerOneFive />
                        </BlogCard>
                        <BlogCard
                            title={'Version 1.4.0'}
                            type={'Release note'}
                            date={'2021-04-30'}
                        >
                            <VerOneFour />
                        </BlogCard>
                        <BlogCard
                            title={'Version 1.3.0'}
                            type={'Release note'}
                            date={'2021-03-12'}
                        >
                            <VerOneThree />
                        </BlogCard>
                        <BlogCard
                            title={'Version 1.2.0(Chrome and Edge)'}
                            type={'Release note'}
                            date={'2021-02-26'}
                        >
                            <VerOneTwo />
                        </BlogCard>
                        <BlogCard
                            title={
                                'Version 1.1.5 of tabExtend now out for Chrome and Edge'
                            }
                            type={'Release note'}
                            date={'2021-02-09'}
                        >
                            <VerOneOneFive />
                        </BlogCard>
                        <BlogCard
                            title={'Version 1.1.0 for Chrome and Edge'}
                            type={'Release note'}
                            date={'2021-02-02'}
                        >
                            <VerOneOne />
                        </BlogCard>
                        <BlogCard
                            title={'Chrome, Firefox and Edge version 1.0'}
                            type={'Release note'}
                            date={'2021-01-18'}
                        >
                            <VerOne />
                        </BlogCard>
                        {/* <BlogCard
              title={"Introducing: A drop zone between pages"}
              type={"About"}
              date={"2020-11-01"}
            >
              <IntroText />
            </BlogCard> */}
                    </div>
                </div>
            </section>
        </>
    );
}

export default UpdatesSection;

/* common, see it all the time
i want to organize, i dont want to, 
Share with family, friends ... 
Sync to the phone
 skratch my own itch
 reduce friction
 save time
 secure
 privacy
 optimize
 ux
 price
 never sell data
 or show ads  */
